import useSWR from "swr";
import { Link } from "react-router-dom";
import type { User } from "@/lib/swr";
import type { App } from "@/types/domain";
import { UserAvatar } from "@/components/user-chip";

// Owner avatars (stacked) at the far right of the app title row; click-through
// to the Settings tab where ownership/members are managed. Hidden while
// ownerless.
export const AppOwnerAvatar = ({ app }: { app: App }) => {
  const { data: users } = useSWR<User[]>(["/users"]);
  const ids = (app.ownerIds ?? []).map(String);
  const owners = (users ?? []).filter((u) => ids.includes(String(u.id)));
  if (!owners.length) return null;
  return (
    <Link
      to={`/apps/${app.id}/settings`}
      title={`Owner: ${owners.map((o) => o.name).join(", ")}`}
      className="flex shrink-0 items-center"
    >
      {owners.map((o) => (
        <UserAvatar
          key={o.id}
          user={o}
          className="-ml-1.5 h-7 w-7 border-2 border-background first:ml-0"
        />
      ))}
    </Link>
  );
};

// App detail header: name + id + group, display only — editing (name/group,
// ownership, members) lives in the Settings tab.
export const AppHeader = ({ app }: { app: App }) => (
  <header className="min-w-0">
    <div className="flex items-center gap-2">
      <h1 className="text-2xl font-semibold leading-tight">{app.name}</h1>
      <span className="text-sm text-muted-foreground">#{app.id}</span>
      {app.group && (
        <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {app.group}
        </span>
      )}
      {app.isApp && (
        <span className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
          app
        </span>
      )}
    </div>
  </header>
);

export default AppHeader;
